import { encodeFunctionData, getAddress } from "viem";
import { IDENTITY_REGISTRY_ABI } from "../abis.js";
import { getDeployment } from "../addresses.js";
import { unsupported, usage } from "../errors.js";
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
const ERC8004_TYPE = "https://eips.ethereum.org/EIPS/eip-8004#registration-v1";
const B64_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
/**
 * Standard base64 of a UTF-8 string using only ES built-ins (no Buffer / btoa /
 * TextEncoder) so the SDK stays browser-safe and Node-types-free. Byte-identical
 * to `Buffer.from(str, "utf8").toString("base64")` — pinned by the golden mint
 * test against the confirmed on-chain calldata.
 */
function toBase64Utf8(str) {
    // UTF-8 bytes as a Latin1 ("binary") string via encodeURIComponent.
    const bin = encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, h) => String.fromCharCode(parseInt(h, 16)));
    let out = "";
    for (let i = 0; i < bin.length; i += 3) {
        const a = bin.charCodeAt(i);
        const hasB = i + 1 < bin.length;
        const hasC = i + 2 < bin.length;
        const b = hasB ? bin.charCodeAt(i + 1) : 0;
        const c = hasC ? bin.charCodeAt(i + 2) : 0;
        out += B64_ALPHABET[a >> 2];
        out += B64_ALPHABET[((a & 3) << 4) | (b >> 4)];
        out += hasB ? B64_ALPHABET[((b & 15) << 2) | (c >> 6)] : "=";
        out += hasC ? B64_ALPHABET[c & 63] : "=";
    }
    return out;
}
/**
 * Encode an ERC-8004 agent-identity mint as a single `register(agentURI, [])`
 * call against the IdentityRegistry — the same on-chain shape Agent0's
 * `registerOnChain()` produces, but built with viem only (no Agent0 SDK / IPFS).
 *
 * `agentURI` is a `data:application/json;base64,…` URI carrying the ERC-8004
 * registration JSON. The registry assigns the next token id and stores the URI;
 * the soft metadata fields don't affect the mint. The minted `chainId:tokenId`
 * is referenced cross-chain when creating/joining funds.
 *
 * ERC-8004 lives on Base only — throws UNSUPPORTED on other chains.
 */
export function encodeIdentityMint(args, chainId) {
    const registry = getDeployment(chainId).identityRegistry;
    if (registry === ZERO_ADDRESS) {
        throw unsupported(`ERC-8004 identity registry is not deployed on chain ${chainId} — mint on Base (8453).`);
    }
    if (!args.name)
        throw usage("`name` is required");
    if (!args.description)
        throw usage("`description` is required");
    // Key order is significant for byte-stable output — mirrors Agent0's
    // on-chain data-URI registration JSON: type, name, description, image?,
    // services, active, x402Support.
    const reg = {
        type: ERC8004_TYPE,
        name: args.name,
        description: args.description,
    };
    if (args.image)
        reg.image = args.image;
    reg.services = [];
    reg.active = args.active ?? true;
    reg.x402Support = args.x402Support ?? false;
    const json = JSON.stringify(reg);
    const dataUri = `data:application/json;base64,${toBase64Utf8(json)}`;
    return {
        txs: [
            {
                to: getAddress(registry),
                data: encodeFunctionData({
                    abi: IDENTITY_REGISTRY_ABI,
                    functionName: "register",
                    args: [dataUri, []],
                }),
                value: "0x0",
                chainId,
            },
        ],
        preconditions: [],
        description: `Mint an ERC-8004 agent identity "${args.name}" on chain ${chainId}.`,
        note: "ERC-8004 identity is minted on Base. Read the resulting token id from the mint receipt (chainId:tokenId); reference it when creating or joining funds.",
    };
}
